type PhaseWithDuration = {
  id: string
  duration: number
}

const buildIdentityOrder = (phaseCount: number) =>
  Array.from({ length: phaseCount }, (_, index) => index)

const isValidPhaseOrder = (order: number[], phaseCount: number) => {
  if (order.length !== phaseCount) return false
  const seen = new Set<number>()
  for (const index of order) {
    if (!Number.isInteger(index) || index < 0 || index >= phaseCount)
      return false
    if (seen.has(index)) return false
    seen.add(index)
  }
  return true
}

export const normalizePhaseOrder = (
  phaseCount: number,
  phaseOrder?: number[] | null,
) => {
  if (!phaseOrder || !isValidPhaseOrder(phaseOrder, phaseCount)) {
    return buildIdentityOrder(phaseCount)
  }
  return phaseOrder
}

export const buildPhaseStartsInOrder = (
  phaseDurations: number[],
  order: number[],
) => {
  const starts = new Array(phaseDurations.length).fill(0)
  let running = 0
  for (const index of order) {
    starts[index] = running
    running += phaseDurations[index] ?? 0
  }
  return starts
}

const buildPhaseStarts = (phaseDurations: number[]) => {
  return buildPhaseStartsInOrder(
    phaseDurations,
    buildIdentityOrder(phaseDurations.length),
  )
}

const toPhaseDurationSeconds = (duration: number) =>
  Math.max(Math.round(duration), 0)

export const getDefaultPhaseDurations = (phases: PhaseWithDuration[]) => {
  return phases.map((phase) => toPhaseDurationSeconds(phase.duration))
}

export const resolveEffectivePhaseDurations = (
  phases: PhaseWithDuration[],
  persistedPhaseDurations: unknown,
) => {
  const persisted =
    typeof persistedPhaseDurations === 'object' &&
    persistedPhaseDurations !== null &&
    !Array.isArray(persistedPhaseDurations)
      ? persistedPhaseDurations
      : {}
  return phases.map((phase) => {
    const defaultDuration = toPhaseDurationSeconds(phase.duration)
    const persistedDuration = (persisted as Record<string, unknown>)[phase.id]
    if (typeof persistedDuration !== 'number') return defaultDuration
    return Math.max(Math.round(persistedDuration), 0)
  })
}

export const resolvePhaseIndexAtTime = (
  at: number,
  phaseStarts: number[],
  phaseEnds: number[],
  phaseOrder: number[],
) => {
  for (const phaseIndex of phaseOrder) {
    const phaseEnd = phaseEnds[phaseIndex]
    if (phaseEnd === undefined) continue
    if (at < phaseEnd) return phaseIndex
  }
  return phaseOrder[phaseOrder.length - 1] ?? 0
}

export const resolvePhaseTiming = ({
  phases,
  persistedPhaseDurations,
  phaseOrder,
}: {
  phases: PhaseWithDuration[]
  persistedPhaseDurations: unknown
  phaseOrder?: number[] | null
}) => {
  const defaultPhaseDurations = getDefaultPhaseDurations(phases)
  const effectivePhaseDurations = resolveEffectivePhaseDurations(
    phases,
    persistedPhaseDurations,
  )
  const resolvedPhaseOrder = normalizePhaseOrder(phases.length, phaseOrder)
  const defaultPhaseStarts = buildPhaseStarts(defaultPhaseDurations)
  const phaseStarts = buildPhaseStartsInOrder(
    effectivePhaseDurations,
    resolvedPhaseOrder,
  )
  const phaseEnds = phaseStarts.map(
    (phaseStart, index) => phaseStart + (effectivePhaseDurations[index] ?? 0),
  )

  return {
    defaultPhaseDurations,
    effectivePhaseDurations,
    defaultPhaseStarts,
    phaseStarts,
    phaseEnds,
    phaseOrder: resolvedPhaseOrder,
  }
}
